import { defineStore } from 'pinia'
import { AxiosError } from 'axios'
import type {
  CommandsUploadCertificateResponse,
  QueriesGetSSOOnResponse,
} from 'client/identity'
import { HNRV2, fileNetworkRequest } from '~/helpers'
import { openDownloadFileDialogWithData } from '~/helpers/file'
import { notifyFailure, notifySuccess } from '~/utils/notifications'
import { api } from '~/services'
import { getApiEndpoint } from '~/helpers/api'

export interface SSOState {
  enableSSO: boolean
  entityName: string
  ssoLoginUrl: string
  ssoLogoutUrl: string
  certificate: CommandsUploadCertificateResponse | null
  metadataFile: File | null
  signRequest: boolean
  signAssertion: boolean
  requireSSO: boolean
  useAlternateSSOID: boolean
  alternateSSOID: string
  getSSOConfigurationLoading: boolean
  submitFormLoading: boolean
}
export const useSSOStore = defineStore('sso', {
  state: (): SSOState => {
    return {
      enableSSO: false,
      entityName: '',
      ssoLoginUrl: '',
      ssoLogoutUrl: '',
      certificate: null,
      metadataFile: null,
      signRequest: false,
      signAssertion: false,
      requireSSO: false,
      useAlternateSSOID: false,
      alternateSSOID: '',
      getSSOConfigurationLoading: false,
      submitFormLoading: false,
    }
  },
  getters: {
    submittingWithCertificate(): boolean {
      return (
        !!this.certificate?.certificateName &&
        !!this.certificate?.certificateExpirationDate
      )
    },
    providerSuppliedSSOURL(): string | undefined {
      if (window.__env.PS_SSO_PATH) {
        const apiEndpoint = getApiEndpoint()

        return `${apiEndpoint}${window.__env.PS_SSO_PATH}`
      }
    },
  },
  actions: {
    async getSSOConfiguration(): Promise<boolean> {
      this.getSSOConfigurationLoading = true

      const response = await HNRV2({
        fn: () => api.sso.apiIdentitySSOGet(),
      })

      if (response instanceof AxiosError) {
        notifyFailure('Unable to get SSO configuration settings.')
        return false
      }

      this.enableSSO = response.ssoOn
      this.entityName = response.entityName
      this.ssoLoginUrl = response.loginURL
      this.ssoLogoutUrl = response.logoutURL
      this.requireSSO = response.requireSSO
      this.signAssertion = response.signAssertion
      this.signRequest = response.signRequest
      this.useAlternateSSOID = response.useAlternateSSOID
      this.certificate = {
        certificateName: response.certificateName,
        certificateExpirationDate: response.certificateExpirationDate,
      }
      return true
    },
    async getSSOOnStatus(): Promise<QueriesGetSSOOnResponse | null> {
      this.getSSOConfigurationLoading = true

      const response = await HNRV2({ fn: () => api.sso.apiIdentitySSOOnGet() })

      if (response instanceof AxiosError) {
        notifyFailure('Unable to get SSO settings.')
        return null
      }
      this.enableSSO = response.ssoOn
      this.useAlternateSSOID = response.useAlternateSSOID
      this.requireSSO = response.requireSSO
      return response
    },
    async uploadMetadata(files: File[]): Promise<boolean> {
      const [file] = files
      const response = await HNRV2({
        fn: () => api.sso.apiIdentitySSOUploadPost(file),
      })

      if (response instanceof AxiosError) {
        if (response.message) {
          notifyFailure(response.message)
        } else {
          notifyFailure('Unable to upload metadata file.')
        }
        return false
      }

      this.entityName = response.entityName ?? ''
      this.ssoLoginUrl = response.loginURL ?? ''
      this.ssoLogoutUrl = response.logoutURL ?? ''
      this.certificate = {
        certificateName: response.certificateName,
        certificateExpirationDate: response.certificateExpirationDate,
      }
      this.metadataFile = file
      return true
    },
    async deleteCertificate(): Promise<boolean> {
      const response = await HNRV2({ fn: () => api.sso.apiIdentitySSODelete() })

      if (response instanceof AxiosError) {
        notifyFailure('Unable to delete certificate.')
        return false
      }

      return true
    },
    async uploadCertificate(files: File[]) {
      const [file] = files
      const response = await HNRV2({
        fn: () => api.sso.apiIdentitySSOCertificatePost(file),
      })

      if (response instanceof AxiosError) {
        notifyFailure('Unable to upload metadata file.')
        return false
      }

      this.certificate = response
      notifySuccess('Successfully updated certificate.')
      return true
    },
    async submit(certificateExists: boolean): Promise<boolean> {
      this.submitFormLoading = true
      try {
        if (!this.submittingWithCertificate && certificateExists) {
          await this.deleteCertificate()
        }
        const response = await HNRV2({
          fn: () =>
            api.sso.apiIdentitySSOPost({
              entityName: this.entityName,
              loginURL: this.ssoLoginUrl,
              logoutURL: this.ssoLogoutUrl,
              requireSSO: this.requireSSO,
              signAssertion: this.signAssertion,
              signRequest: this.signRequest,
              ssoOn: this.enableSSO,
              useAlternateSSOID: this.useAlternateSSOID,
            }),
        })

        if (response instanceof AxiosError) {
          notifyFailure('Unable to save SSO configuration.')
          return false
        }

        notifySuccess('Successfully saved SSO configuration.')
        return true
      } finally {
        this.submitFormLoading = false
      }
    },
    async downloadMetadataFile() {
      const response = await fileNetworkRequest({
        fn: () => api.ssoFile.apiIdentitySSODownloadPost(),
      })

      if (response instanceof AxiosError) {
        notifyFailure('Unable to download metadata file.')
        return
      }

      openDownloadFileDialogWithData({
        response,
      })
      notifySuccess('Successfully downloaded Metadata file.')
      return
    },
    async getUnelevatedSSOData(): Promise<void> {
      const response = await HNRV2({
        fn: () => api.sso.apiIdentitySSOClientSsoInfoGet(),
      })

      if (response instanceof AxiosError) {
        return
      }

      this.enableSSO = response.ssoOn
      this.requireSSO = response.ssoRequired
    },
  },
})
